import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemAvatar,
  Avatar,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  ListItemText,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Medication as MedicationIcon,
  Logout as LogoutIcon,
  Warning as WarningIcon,
  Map as GeospatialIcon,
  CloudUpload as CloudUploadIcon,
  Add as AddIcon
} from '@mui/icons-material';
import PatientForm from './PatientForm';
import PatientADRView from './PatientADRView';
import MedicationForm from './MedicationForm';
import GeospatialMap from './GeospatialMap';
import { useNavigate } from 'react-router-dom';

// Import Firestore service and Excel upload component
import { getAllPatients } from '../../services/firestore';
import PatientExcelUpload from '../../components/PatientExcelUpload';
import { medicationData, adverseReactionData } from './mockData';

// Define Patient interface
interface Patient {
  id: string;
  name: string;
  treatmentType: string;
  adherenceRate: number;
  missedDoses: number;
  confirmedDoses: number;
  pendingDoses: number;
  startDate: string;
  nextAppointment: string;
  municipality: string;
  province: string;
  age: number;
  sex: string;
  civilStatus: string;
  philHealthNumber: string;
  phoneNumber: string;
}

const drawerWidth = 240;

// Add this helper function before the Dashboard component
const getAdjustedDate = (dateString: string) => {
  const date = new Date(dateString);
  const currentDate = new Date();
  
  // If the date is in the past
  if (date < currentDate) {
    // Set to current date + 1 month
    const futureDate = new Date();
    futureDate.setMonth(futureDate.getMonth() + 1);
    return futureDate;
  }
  
  return date;
};

const Dashboard: React.FC = () => {

  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [patientFormOpen, setPatientFormOpen] = useState(false);
  const [medicationFormOpen, setMedicationFormOpen] = useState(false);
  const [patientsData, setPatientsData] = useState<Patient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [medications] = useState(medicationData);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [adrViewOpen, setAdrViewOpen] = useState(false);
  const [selectedReaction, setSelectedReaction] = useState<any>(null);
  const [submittedReactions] = useState<any[]>(adverseReactionData);
  const [showUploadForm, setShowUploadForm] = useState(false);

  // Fetch patients from Firestore
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const patients = await getAllPatients();
        setPatientsData(patients as any);
      } catch (err) {
        console.error('Error fetching patients:', err);
        setError('Failed to load patient data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatients();
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    navigate('/login');
  };

  // Handle patient selection for viewing details
  const handleViewPatient = (patient: Patient) => {
    console.log("View patient clicked:", patient);
    
    // Create a properly structured patient object using the actual patient data
    const formattedPatient = {
      basicInfo: {
        surname: patient.name.split(' ')[1] || '',
        givenName: patient.name.split(' ')[0] || '',
        middleName: patient.name.split(' ')[2] || '',
        nameExtension: '',
        age: patient.age || 45,
        dateOfBirth: patient.startDate,
        sex: patient.sex || 'M',
        civilStatus: patient.civilStatus || 'married',
        philHealthNumber: patient.philHealthNumber || '01-234567890-1',
        address: {
          municipality: patient.municipality || 'Quezon City',
          province: patient.province || 'Metro Manila',
          phoneNumber: patient.phoneNumber || '09123456789',
          smsAvailable: true
        }
      },
      healthConditions: {
        hivStatus: 'no',
        diabetes: 'no',
        renalDisease: 'no',
        maintenanceMedication: {
          taking: 'no',
          medications: ''
        },
        healthIssues: {
          alcohol: { drinks: 'no', current: 'no' },
          tobacco: { uses: 'no', current: 'no' },
          illicitDrugs: { used: 'no', current: 'no' }
        }
      },
      diagnosis: {
        tbCaseNumber: `TB-2024-${patient.id}`,
        diagnosisType: 'TB Disease',
        dateOfDiagnosis: patient.startDate,
        classification: 'Pulmonary',
        confirmationType: 'Bacteriologically-confirmed',
        treatmentType: patient.treatmentType,
        location: 'Facility-based',
        treatmentHistory: { prior: 'no', details: '' },
        currentRegimen: 'HRZE Fixed-dose combination',
        monitoring: {
          startDate: patient.startDate,
          supporterName: 'Treatment Supporter',
          supporterContact: '09187654321',
          dotStatus: 'Treatment Supporter Supervised'
        }
      },
      adverseReactions: {
        lastOccurrence: '',
        reactions: [],
        emergencyAlerts: []
      },
      followUps: {
        missedDose: {
          enableAlerts: true,
          lastMissed: '',
          reason: ''
        },
        notifications: {
          notifyPatient: true,
          notifySupporter: true,
          notifyTBPeople: false
        },
        visits: {
          scheduledDate: patient.nextAppointment,
          lastVisit: patient.startDate,
          enableAlerts: true,
          requestFollowUp: false
        }
      }
    };
    
    setSelectedPatient(formattedPatient);
    setSelectedPatientId(patient.id);
    setPatientFormOpen(true);
  };

  // Handle closing the patient form
  const handleClosePatientForm = () => {
    setPatientFormOpen(false);
    setSelectedPatient(null);
    setSelectedPatientId(null);
  };

  // Handle successful upload
  const handleUploadSuccess = async (count: number) => {
    // Refresh the patient data
    try {
      setIsLoading(true);
      const patients = await getAllPatients();
      setPatientsData(patients as any);
      
      // Hide the upload form after successful import
      setTimeout(() => {
        setShowUploadForm(false);
      }, 3000);
    } catch (err) {
      console.error('Error refreshing patients after upload:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle the upload form
  const toggleUploadForm = () => {
    setShowUploadForm(prev => !prev);
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0a1c30', color: 'white' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          component="img"
          src="/tbx-title.png"
          alt="TBX Logo"
          sx={{ height: 40, width: 'auto', mr: 1 }}
        />
      </Box>
      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
      <List>
        <ListItem 
          sx={{ 
            cursor: 'pointer',
            bgcolor: activeSection === 0 ? 'rgba(255,255,255,0.1)' : 'transparent'
          }}
          onClick={() => {
            setActiveSection(0);
            if (mobileOpen) setMobileOpen(false);
          }}
        >
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: 'transparent' }}>
              <DashboardIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText primary="Overview" />
        </ListItem>
        <ListItem 
          sx={{ 
            cursor: 'pointer',
            bgcolor: activeSection === 1 ? 'rgba(255,255,255,0.1)' : 'transparent'
          }}
          onClick={() => {
            setActiveSection(1);
            if (mobileOpen) setMobileOpen(false);
          }}
        >
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: 'transparent' }}>
              <PersonIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText primary="Patients" />
        </ListItem>
        <ListItem 
          sx={{ 
            cursor: 'pointer',
            bgcolor: activeSection === 2 ? 'rgba(255,255,255,0.1)' : 'transparent'
          }}
          onClick={() => {
            setActiveSection(2);
            if (mobileOpen) setMobileOpen(false);
          }}
        >
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: 'transparent' }}>
              <MedicationIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText primary="Medications" />
        </ListItem>
        <ListItem 
          sx={{ 
            cursor: 'pointer',
            bgcolor: activeSection === 3 ? 'rgba(255,255,255,0.1)' : 'transparent'
          }}
          onClick={() => {
            setActiveSection(3);
            if (mobileOpen) setMobileOpen(false);
          }}
        >
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: 'transparent' }}>
              <WarningIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText primary="Adverse Reactions" />
        </ListItem>
        <ListItem 
          sx={{ 
            cursor: 'pointer',
            bgcolor: activeSection === 4 ? 'rgba(255,255,255,0.1)' : 'transparent'
          }}
          onClick={() => {
            setActiveSection(4);
            if (mobileOpen) setMobileOpen(false);
          }}
        >
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: 'transparent' }}>
              <GeospatialIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText primary="Geospatial Map" />
        </ListItem>
      </List>
      <Box sx={{ mt: 'auto', p: 2 }}>
        <Button 
          variant="outlined" 
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          fullWidth
          sx={{ color: 'white', borderColor: 'white' }}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  // Render content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case 0: // Overview
        // Calculate summary statistics
        const totalPatients = patientsData.length;
        const averageAdherence = Math.round(patientsData.reduce((sum, p) => sum + p.adherenceRate, 0) / totalPatients);
        const totalConfirmedDoses = patientsData.reduce((sum, p) => sum + p.confirmedDoses, 0);
        const totalMissedDoses = patientsData.reduce((sum, p) => sum + p.missedDoses, 0);
        
        // Weekly adherence data (mock data for visualization)
        const weeklyAdherenceData = [
          { week: 'Week 1', adherent: 90, missed: 10 },
          { week: 'Week 2', adherent: 85, missed: 15 },
          { week: 'Week 3', adherent: 75, missed: 25 },
          { week: 'Week 4', adherent: 80, missed: 20 },
        ];
        
        return (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h4" gutterBottom>
                Dashboard Overview
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={toggleUploadForm}
                  startIcon={<CloudUploadIcon />}
                >
                  {showUploadForm ? 'Hide Upload' : 'Import Patients'}
                </Button>
            
              </Box>
            </Box>

            {showUploadForm && (
              <PatientExcelUpload 
                onUploadSuccess={handleUploadSuccess} 
                onUploadError={(msg) => setError(msg)}
              />
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Summary Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%', borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                          Total Patients
                        </Typography>
                        <Typography variant="h2" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                          {totalPatients}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Active in treatment
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%', borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                          Overall Adherence
                        </Typography>
                        <Typography variant="h2" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                          {averageAdherence}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Across all patients
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%', borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                          Doses Confirmed
                        </Typography>
                        <Typography variant="h2" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                          {totalConfirmedDoses}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 7 days
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%', borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                          Doses Missed
                        </Typography>
                        <Typography variant="h2" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                          {totalMissedDoses}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 7 days
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                
                {/* Adherence Visualizations */}
                <Grid container spacing={3}>
                  {/* Overall Adherence Rate */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box 
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              borderRadius: '50%', 
                              bgcolor: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 1
                            }}
                          >
                            <Box 
                              component="span" 
                              sx={{ 
                                width: 12, 
                                height: 12, 
                                borderRadius: '50%', 
                                bgcolor: 'white',
                                display: 'block'
                              }}
                            />
                          </Box>
                          <Typography variant="h6">Overall Adherence Rate</Typography>
                        </Box>
                        
                        <Box sx={{ my: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body1">Adherent</Typography>
                            <Typography variant="body1" fontWeight="bold">{averageAdherence}%</Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={averageAdherence} 
                            sx={{ 
                              height: 10, 
                              borderRadius: 5,
                              bgcolor: '#e0e0e0',
                              mb: 3,
                              '& .MuiLinearProgress-bar': {
                                bgcolor: '#4caf50'
                              }
                            }}
                          />
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body1">Missed</Typography>
                            <Typography variant="body1" fontWeight="bold">{100 - averageAdherence - 10}%</Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={100 - averageAdherence - 10} 
                            sx={{ 
                              height: 10, 
                              borderRadius: 5,
                              bgcolor: '#e0e0e0',
                              mb: 3,
                              '& .MuiLinearProgress-bar': {
                                bgcolor: '#f44336'
                              }
                            }}
                          />
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body1">Pending</Typography>
                            <Typography variant="body1" fontWeight="bold">10%</Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={10} 
                            sx={{ 
                              height: 10, 
                              borderRadius: 5,
                              bgcolor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: '#ff9800'
                              }
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Weekly Adherence Trend */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <Box 
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              borderRadius: '2px', 
                              bgcolor: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 1
                            }}
                          >
                            <Box 
                              component="span" 
                              sx={{ 
                                width: 12, 
                                height: 12, 
                                borderRadius: '1px', 
                                bgcolor: 'white',
                                display: 'block'
                              }}
                            />
                          </Box>
                          <Typography variant="h6">Weekly Adherence Trend</Typography>
                        </Box>
                        
                        {weeklyAdherenceData.map((week, index) => (
                          <Box key={index} sx={{ mb: 3 }}>
                            <Typography variant="body1" sx={{ mb: 1 }}>{week.week}</Typography>
                            <Box sx={{ display: 'flex', height: 24, borderRadius: 1, overflow: 'hidden' }}>
                              <Box 
                                sx={{ 
                                  width: `${week.adherent}%`, 
                                  bgcolor: '#4caf50',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontSize: '0.75rem'
                                }}
                              >
                                {week.adherent}%
                              </Box>
                              <Box 
                                sx={{ 
                                  width: `${week.missed}%`, 
                                  bgcolor: '#f44336',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontSize: '0.75rem'
                                }}
                              >
                                {week.missed}%
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Recent Patients */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardHeader title="Recent Patients" />
                      <CardContent>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Treatment</TableCell>
                                <TableCell>Adherence</TableCell>
                                <TableCell>Next Appointment</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {patientsData.slice(0, 5).map((patient) => (
                                <TableRow key={patient.id}>
                                  <TableCell>{patient.name}</TableCell>
                                  <TableCell>Type {patient.treatmentType}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={`${patient.adherenceRate}%`} 
                                      size="small"
                                      color={patient.adherenceRate >= 90 ? "success" : patient.adherenceRate >= 80 ? "warning" : "error"}
                                    />
                                  </TableCell>
                                  <TableCell>{getAdjustedDate(patient.nextAppointment).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                      <CardActions>
                        <Button size="small" onClick={() => setActiveSection(1)}>View All Patients</Button>
                      </CardActions>
                    </Card>
                  </Grid>
                  
                  {/* Recent Adverse Reactions */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardHeader title="Recent Adverse Reactions" />
                      <CardContent>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Patient</TableCell>
                                <TableCell>Reaction</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {submittedReactions.slice(0, 5).map((reaction, index) => (
                                <TableRow key={index}>
                                  <TableCell>{reaction.patientName}</TableCell>
                                  <TableCell>{reaction.reaction}</TableCell>
                                  <TableCell>{reaction.date}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={reaction.status} 
                                      size="small"
                                      color={reaction.isEmergency ? "error" : "primary"}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                      <CardActions>
                        <Button size="small" onClick={() => setActiveSection(3)}>View All Reactions</Button>
                      </CardActions>
                    </Card>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        );
      
      case 1: // Patients
        return (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h4" gutterBottom>
                Patient Management
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={toggleUploadForm}
                  startIcon={<CloudUploadIcon />}
                >
                  {showUploadForm ? 'Hide Upload' : 'Import Patients'}
                </Button>
                <Button 
                  variant="contained" 
                  onClick={() => setPatientFormOpen(true)}
                  startIcon={<AddIcon />}
                >
                  Add Patient
                </Button>
              </Box>
            </Box>

            {showUploadForm && (
              <PatientExcelUpload 
                onUploadSuccess={handleUploadSuccess} 
                onUploadError={(msg) => setError(msg)}
              />
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3} sx={{ mx: 0, width: '100%' }}>
                {patientsData.map((patient) => (
                  <Grid item xs={12} lg={6} key={patient.id}>
                    <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6">{patient.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Treatment Type: {patient.treatmentType} | Started: {patient.startDate}
                          </Typography>
                        </Box>
                        <Chip 
                          label={`${patient.adherenceRate}% Adherence`} 
                          color={patient.adherenceRate >= 90 ? "success" : patient.adherenceRate >= 80 ? "warning" : "error"}
                        />
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>Adherence Progress</Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={patient.adherenceRate} 
                          sx={{ 
                            height: 10, 
                            borderRadius: 5,
                            bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: patient.adherenceRate >= 90 ? "#4caf50" : patient.adherenceRate >= 80 ? "#ff9800" : "#f44336"
                            }
                          }}
                        />
                      </Box>
                      
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">Missed Doses</Typography>
                          <Typography variant="h6" color="error">{patient.missedDoses}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">Confirmed</Typography>
                          <Typography variant="h6" color="success.main">{patient.confirmedDoses}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">Pending</Typography>
                          <Typography variant="h6" color="warning.main">{patient.pendingDoses}</Typography>
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2">
                          Next Appointment: <strong>{getAdjustedDate(patient.nextAppointment).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}</strong>
                        </Typography>
                        <Button 
                          variant="outlined" 
                          size="small"
                          onClick={() => handleViewPatient(patient)}
                        >
                          View Details
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        );
      
      case 2: // Medications
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
              Medication Management
            </Typography>
            <Typography variant="body1" paragraph>
              View and manage medications, dosages, and patient prescriptions.
            </Typography>
            
            <Grid container spacing={3}>
              {medications.map((medication) => (
                <Grid item xs={12} md={6} lg={4} key={medication.id}>
                  <Card>
                    <CardHeader 
                      title={medication.name} 
                      subheader={`${medication.dosage} - ${medication.frequency}`}
                    />
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Prescribed to {medication.patients} patients
                      </Typography>
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                        <Button 
                          variant="outlined" 
                          size="small"
                          onClick={() => setMedicationFormOpen(true)}
                        >
                          View Details
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      
      case 3: // Adverse Reactions
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
              Adverse Drug Reactions
            </Typography>
            <Typography variant="body1" paragraph>
              Monitor and manage adverse drug reactions reported by patients.
            </Typography>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Patient</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Reaction</TableCell>
                    <TableCell>Drugs</TableCell>
                    <TableCell>Management</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submittedReactions.map((reaction, index) => (
                    <TableRow key={index} sx={{ bgcolor: reaction.isEmergency ? 'rgba(244, 67, 54, 0.1)' : 'inherit' }}>
                      <TableCell>{reaction.patientName}</TableCell>
                      <TableCell>{reaction.date}</TableCell>
                      <TableCell>{reaction.reaction}</TableCell>
                      <TableCell>{reaction.drugs}</TableCell>
                      <TableCell>{reaction.management}</TableCell>
                      <TableCell>
                        <Chip 
                          label={reaction.status} 
                          color={reaction.isEmergency ? "error" : "primary"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="outlined" 
                          size="small"
                          onClick={() => {
                            // Find the patient associated with this reaction
                            const patient = patientsData.find(p => p.id === reaction.patientId);
                            if (patient) {
                              handleViewPatient(patient);
                              setSelectedReaction(reaction);
                              setAdrViewOpen(true);
                            }
                          }}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      
      case 4: // Geospatial Map
        return (
          <Box sx={{ p: 0 }}>
            <GeospatialMap patientData={patientsData} />
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: '#0a1c30'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: 'white' }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            component="img"
            src="/tbx-title.png"
            alt="TBX Logo"
            sx={{ height: 32, width: 'auto', mr: 1, display: { xs: 'block', sm: 'none' } }}
          />
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box component="main" sx={{ flexGrow: 1, p: 0, width: { sm: `calc(100% - ${drawerWidth}px)` } }}>
        <Toolbar />
        
        {/* Main content based on active section */}
        {renderContent()}
        
        {/* Patient Details Form */}
        <PatientForm 
          open={patientFormOpen} 
          onClose={handleClosePatientForm} 
          patient={selectedPatient}
          patientId={selectedPatientId}
        />
        
        {/* Patient ADR View */}
        <PatientADRView
          open={adrViewOpen}
          onClose={() => setAdrViewOpen(false)}
          patient={selectedPatient}
          reaction={selectedReaction}
        />
        
        {/* Medication Form */}
        <MedicationForm
          open={medicationFormOpen}
          onClose={() => setMedicationFormOpen(false)}
        />
      </Box>
    </Box>
  );
};

export default Dashboard;